/**
 * Promo code picker for a CTA.
 *
 * WHY A PICKER AND NOT FREE TEXT
 * Measured on PROD before this shipped: of 2,679 CTAs, 2,666 carried a promo value and 2,582 of
 * those (97%) were the organization's own code, typed by hand. The value was never linked to the
 * organization — copying it correctly was left to the operator.
 *
 * WHY AN UNKNOWN VALUE IS STILL SHOWN
 * Migration 2026-09-16-024 backfilled a promo_codes row for every code an existing CTA already
 * used, so in normal operation the current value is always in the list. If it somehow is not
 * (a code deactivated after the CTA was built, say), it is surfaced as a clearly-labelled entry
 * and kept selected rather than silently swapped for something else — quietly rewriting a live
 * CTA's promo code is worse than showing an odd-looking option.
 */
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../../services/supabase";

interface PromoCode {
  id: string;
  code: string;
  description: string | null;
  is_default: boolean;
}

interface Props {
  organizationId: string;
  value: string;
  onChange: (code: string) => void;
  /** Shown under the control; the CTA surfaces differ slightly in wording. */
  helpText?: string;
}

export const useOrganizationPromoCodes = (organizationId: string) =>
  useQuery({
    queryKey: ["organization-promo-codes", organizationId],
    enabled: !!organizationId,
    queryFn: async (): Promise<PromoCode[]> => {
      const { data, error } = await supabase
        .from("promo_codes")
        .select("id, code, description, is_default")
        .eq("organization_id", organizationId)
        .eq("is_active", true)
        .order("is_default", { ascending: false })
        .order("code", { ascending: true });
      if (error) {
        throw error;
      }
      return (data ?? []) as PromoCode[];
    },
  });

const PromoCodeSelect: React.FC<Props> = ({
  organizationId,
  value,
  onChange,
  helpText,
}) => {
  const { data: codes = [], isLoading } =
    useOrganizationPromoCodes(organizationId);

  const known = codes.some((c) => c.code === value);
  const hasValue = (value ?? "").trim() !== "";

  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        Promo Code
      </label>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={isLoading}
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
      >
        <option value="">
          {isLoading ? "Loading promo codes..." : "— None —"}
        </option>
        {codes.map((c) => (
          <option key={c.id} value={c.code}>
            {c.code}
            {c.is_default ? " (selected code)" : ""}
          </option>
        ))}
        {hasValue && !known && !isLoading && (
          <option value={value}>{value} — not in this organization</option>
        )}
      </select>
      {hasValue && !known && !isLoading && (
        <p className="mt-1 text-xs text-amber-700">
          This CTA uses a code that is not on this organization. It has been
          left as-is; pick another only if you intend to change what the link
          sends.
        </p>
      )}
      {helpText && <p className="mt-1 text-xs text-gray-500">{helpText}</p>}
      {!isLoading && codes.length === 0 && (
        <p className="mt-1 text-xs text-gray-500">
          This organization has no promo codes yet. Add one on the Promo Codes
          tab, then it can be selected here.
        </p>
      )}
    </div>
  );
};

export default PromoCodeSelect;
